import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Client, Lead, Document } from "@fleetlease/shared";
import { useList, useCreate, useUpdate } from "./data";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../lib/api";
import { uploadDocument } from "../lib/uploads";
import { PageHeader, Button, Table, Modal, Field, Input, Select, useForm, FormError } from "./ui";
import { TableSkeleton } from "./ui";
import { Badge } from "./Badge";

const blank = { firstName: "", lastName: "", phone: "", email: "", address: "", status: "ACTIVE" };

export function Clients() {
  const clients = useList<Client>("clients", "/clients");
  const leads = useList<Lead>("leads", "/leads");
  const create = useCreate<any>("/clients", ["clients"]);
  const update = useUpdate<any>((id) => `/clients/${id}`, ["clients"]);
  const [editing, setEditing] = useState<Client | null>(null);
  const [open, setOpen] = useState(false);
  const [docsFor, setDocsFor] = useState<Client | null>(null);
  const f = useForm({ ...blank });
  const mut = editing ? update : create;

  function openCreate() { setEditing(null); f.setValues({ ...blank }); setOpen(true); }
  function openEdit(c: Client) {
    setEditing(c);
    f.setValues({ firstName: c.firstName, lastName: c.lastName, phone: c.phone, email: c.email ?? "", address: c.address ?? "", status: c.status });
    setOpen(true);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const body: any = { firstName: f.values.firstName, lastName: f.values.lastName, phone: f.values.phone, status: f.values.status };
    if (f.values.email) body.email = f.values.email;
    if (f.values.address) body.address = f.values.address;
    if (editing) await update.mutateAsync({ id: editing.id, body });
    else await create.mutateAsync(body);
    setOpen(false);
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Clients & Leads" action={<Button onClick={openCreate}>Add client</Button>} />

      {clients.isPending ? <TableSkeleton cols={5} /> : (
        <Table<Client>
          rows={clients.data?.items ?? []} empty="No clients yet — add your first."
          columns={[
            { header: "Name", cell: (c) => `${c.firstName} ${c.lastName}` },
            { header: "Phone", cell: (c) => c.phone },
            { header: "Email", cell: (c) => c.email ?? "—" },
            { header: "Status", cell: (c) => <Badge tone={c.status === "ACTIVE" ? "info" : "warn"}>{c.status}</Badge> },
            { header: "", cell: (c) => (
              <div className="flex gap-3">
                <button onClick={() => openEdit(c)} className="text-accent hover:underline text-sm">Edit</button>
                <button onClick={() => setDocsFor(c)} className="text-accent hover:underline text-sm">License / docs</button>
              </div>
            ) },
          ]}
        />
      )}

      <div>
        <h2 className="text-sm font-semibold text-ink-muted mb-2">Leads</h2>
        {leads.isPending ? <TableSkeleton cols={4} rows={3} /> : <Table<Lead>
          rows={leads.data?.items ?? []} empty="No leads captured yet."
          columns={[
            { header: "Name", cell: (l) => l.name },
            { header: "Phone", cell: (l) => l.phone },
            { header: "Source", cell: (l) => l.source },
            { header: "Status", cell: (l) => <Badge tone="info">{l.status}</Badge> },
          ]}
        />}
      </div>

      <Modal title={editing ? "Edit client" : "Add client"} open={open} onClose={() => setOpen(false)}>
        <form onSubmit={onSubmit}>
          <FormError error={mut.error} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name"><Input required value={f.values.firstName} onChange={(e) => f.set("firstName", e.target.value)} /></Field>
            <Field label="Last name"><Input required value={f.values.lastName} onChange={(e) => f.set("lastName", e.target.value)} /></Field>
          </div>
          <Field label="Phone"><Input required value={f.values.phone} onChange={(e) => f.set("phone", e.target.value)} placeholder="+16125550100" /></Field>
          <Field label="Email (optional)"><Input type="email" value={f.values.email} onChange={(e) => f.set("email", e.target.value)} /></Field>
          <Field label="Address (optional)"><Input value={f.values.address} onChange={(e) => f.set("address", e.target.value)} /></Field>
          {editing && <Field label="Status"><Select value={f.values.status} onChange={(e) => f.set("status", e.target.value)}>{["ACTIVE", "ARCHIVED"].map((x) => <option key={x}>{x}</option>)}</Select></Field>}
          <div className="mt-2 flex justify-end"><Button type="submit" disabled={mut.isPending}>{mut.isPending ? "Saving…" : editing ? "Save changes" : "Save client"}</Button></div>
        </form>
      </Modal>

      {docsFor && <ClientDocs client={docsFor} onClose={() => setDocsFor(null)} />}
    </div>
  );
}

function ClientDocs({ client, onClose }: { client: Client; onClose: () => void }) {
  const { token } = useAuth();
  const qc = useQueryClient();
  const docs = useQuery({
    queryKey: ["docs", client.id],
    queryFn: () => apiFetch<{ items: Document[] }>(`/clients/${client.id}/documents`, { token }),
    enabled: !!token,
  });
  const [file, setFile] = useState<File | null>(null);
  const [docType, setDocType] = useState("LICENSE_FRONT");
  const [expirationDate, setExp] = useState("");
  const [licenseNumber, setLic] = useState("");
  const [licenseState, setState] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onUpload(e: FormEvent) {
    e.preventDefault();
    if (!file) { setErr("Choose a file first."); return; }
    setErr(null); setBusy(true);
    try {
      await uploadDocument(token, { clientId: client.id, file, docType, expirationDate, licenseNumber, licenseState });
      setFile(null); setExp(""); setLic(""); setState("");
      await docs.refetch();
      qc.invalidateQueries({ queryKey: ["clients"] }); // license affects the rental gate
    } catch (e: any) { setErr(e?.message ?? "Upload failed."); }
    finally { setBusy(false); }
  }

  async function view(key: string) {
    const r = await apiFetch<{ url: string }>(`/documents/presign-view?key=${encodeURIComponent(key)}`, { token });
    window.open(r.url, "_blank");
  }

  const isLicense = docType.startsWith("LICENSE");

  return (
    <Modal title={`Documents — ${client.firstName} ${client.lastName}`} open onClose={onClose}>
      <Table<Document>
        rows={docs.data?.items ?? []} empty="No documents uploaded."
        columns={[
          { header: "Type", cell: (d) => d.docType },
          { header: "File", cell: (d) => d.fileName },
          { header: "Expires", cell: (d) => d.expirationDate ?? "—" },
          { header: "", cell: (d) => <button onClick={() => view(d.s3Key)} className="text-accent hover:underline text-sm">View</button> },
        ]}
      />
      <form onSubmit={onUpload} className="mt-4 border-t border-black/5 pt-4">
        <div className="text-sm font-semibold mb-2">Upload a document</div>
        {err && <div className="mb-2 text-sm text-red-600">{err}</div>}
        <Field label="Type">
          <Select value={docType} onChange={(e) => setDocType(e.target.value)}>
            {["LICENSE_FRONT", "LICENSE_BACK", "INSURANCE", "RENTAL_AGREEMENT", "SECONDARY_DRIVER", "MISC"].map((x) => <option key={x}>{x}</option>)}
          </Select>
        </Field>
        <Field label="File (jpg/png/pdf, ≤10MB)">
          <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="block w-full text-sm" />
        </Field>
        {isLicense && (
          <div className="grid grid-cols-3 gap-3">
            <Field label="Expiration"><Input type="date" value={expirationDate} onChange={(e) => setExp(e.target.value)} /></Field>
            <Field label="License #"><Input value={licenseNumber} onChange={(e) => setLic(e.target.value)} /></Field>
            <Field label="State"><Input value={licenseState} onChange={(e) => setState(e.target.value)} /></Field>
          </div>
        )}
        <div className="flex justify-end"><Button type="submit" disabled={busy}>{busy ? "Uploading…" : "Upload"}</Button></div>
      </form>
    </Modal>
  );
}
